import { create } from "zustand";
import { persist } from "zustand/middleware";

interface InspectorContent {
  type: string;
  data: unknown;
}

interface UIState {
  sidebarCollapsed: boolean;
  inspectorOpen: boolean;
  inspectorContent: InspectorContent | null;
  theme: "light" | "dark" | "system";
  commandPaletteOpen: boolean;
  mobileMenuOpen: boolean;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  openInspector: (content: InspectorContent) => void;
  closeInspector: () => void;
  setTheme: (theme: "light" | "dark" | "system") => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      inspectorOpen: false,
      inspectorContent: null,
      theme: "system",
      commandPaletteOpen: false,
      mobileMenuOpen: false,

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      openInspector: (content) =>
        set({ inspectorOpen: true, inspectorContent: content }),

      closeInspector: () =>
        set({ inspectorOpen: false, inspectorContent: null }),

      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),

      toggleMobileMenu: () =>
        set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
      closeMobileMenu: () => set({ mobileMenuOpen: false }),

      setTheme: (theme) => {
        set({ theme });
        const root = document.documentElement;
        if (theme === "dark") {
          root.classList.add("dark");
        } else if (theme === "light") {
          root.classList.remove("dark");
        } else {
          const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
          root.classList.toggle("dark", prefersDark);
        }
      },
    }),
    {
      name: "medtechos-ui",
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    }
  )
);
